import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Bot, LayoutDashboard } from "lucide-react";
import { NavLink } from "@/widgets/NavLink";
import { useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/shared/ui/sidebar";
import { CollapsibleNavGroup } from "@/widgets/CollapsibleNavGroup";
import { useAuth, scopeFromRole, type Scope } from "@/contexts/AuthContext";
import { useImpersonationOptional } from "@/contexts/ImpersonationContext";
import type { RoleKey } from "@/shared/lib/rbac";
import { useComingSoonModules } from "@/entities/dashboard";
import {
  isPromotionSoftLinked,
  PROMOTION_SOFTLINK_CAP,
  PROMOTION_SOFTLINK_ROUTE,
} from "@/entities/capability";
import { pickBrandLogo, useBrand } from "@/shared/brand";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { moduleIcon } from "@/entities/billing";
import { useLicenseEnabled } from "@/entities/license";
import {
  isNavItemActive,
  sidebarTreeFor,
  visibleByPartnerPerms,
  visibleByPerms,
  kefuNavOverride,
  type NavItem,
} from "@/widgets/appSidebarNav";
import { usePartnerAccess } from "@/entities/partner";

export function AppSidebar() {
  const { t } = useTranslation();
  const brand = useBrand();
  // 深色皮肤(cyber)优先用品牌深色 logo(logoDarkUrl),没配回落 logoUrl。
  const sidebarLogo = pickBrandLogo(brand, useActiveSkin() === "cyber");
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const currentPath = location.pathname;
  const { permissions, user } = useAuth();
  const { isImpersonating } = useImpersonationOptional();
  // 1002:客服舱没开但历史上拥有过(暂停 / 过期,everOwnedModules)→ 侧栏仍给入口 + 「已停用 · 仅人工处理」小标;
  //   从没拥有过的不出。只影响侧栏,路由门 / 页面用各自判据。
  const kefuNav = useMemo(() => kefuNavOverride(permissions?.modules, permissions), [permissions]);
  const modules = kefuNav.modules;
  // V27 (2026-05-20) — sidebar 按"视图角色"渲染:代客时后端 /me/permissions 返
  // TENANT_ADMIN(身份仍 PLATFORM_ADMIN,留给 banner / 审计 / 退出按钮)。从
  // permissions.role 拿视图角色,fallback 真实身份。scope 从视图角色派生。
  const effectiveRole = ((permissions?.role as RoleKey | undefined) ?? user?.role) as
    | RoleKey
    | undefined;
  const effectiveScope: Scope | undefined = effectiveRole
    ? scopeFromRole(effectiveRole)
    : user?.scope;
  const scope = effectiveScope;
  const role = effectiveRole;

  // OEM W5:只有平台超管(非代客)探一次 GET /platform/license,决定「授权」入口是否出现。
  const licenseEnabled = useLicenseEnabled(scope === "PLATFORM" && !isImpersonating);
  // OEM 第二批 A4:渠道账号按渠道内角色(/me partnerPerms)过滤侧栏。
  const partnerAccess = usePartnerAccess(scope === "PARTNER");
  const partnerPerms = scope === "PARTNER" ? partnerAccess.perms : null;

  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = sidebarRef.current;
    if (!container) return;
    const activeEl = container.querySelector('[data-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [currentPath]);

  // 工作台对平台 / 租户角色可见(平台超管见 platform_dashboard,租户角色见 tenant_dashboard)。
  // OEM F2:渠道管理员没有「/」工作台(点了会被路由闸弹回 /partner),入口不出。
  const dashboardVisible = useMemo(
    () =>
      scope !== "PARTNER" &&
      (!modules ||
        modules.platform_dashboard?.page !== "none" ||
        modules.tenant_dashboard?.page !== "none"),
    [modules, scope]
  );

  // 即将上线模块:catalog 单一来源(/me/modules/coming-soon),仅租户视图露出(平台超管不看产品预告)。
  const { data: comingSoonData } = useComingSoonModules();
  // 推广舱过渡软链(1012):catalog 仍 coming_soon,但侧栏这一项直接落 GEO 管理系统,不挂「即将」标。
  const promoSoftLinked = isPromotionSoftLinked(brand);
  const comingSoonItems: NavItem[] = useMemo(
    () =>
      scope === "TENANT"
        ? (comingSoonData ?? []).map((m) =>
            promoSoftLinked && m.capability === PROMOTION_SOFTLINK_CAP
              ? {
                  title: m.displayName,
                  url: PROMOTION_SOFTLINK_ROUTE,
                  icon: moduleIcon(m.capability),
                }
              : {
                  title: m.displayName,
                  url: `/coming-soon/${m.capability}`,
                  icon: moduleIcon(m.capability),
                  comingSoon: true,
                }
          )
        : [],
    [scope, comingSoonData, promoSoftLinked]
  );

  // 数据驱动分组:顺序 SOT = SIDEBAR_GROUPS(模块级,测试锁死);每组按权限过滤后 length>0 才渲染。
  // 「即将上线」(catalog 驱动,非权限门;AI商业/AI记者/AI推广)收编进「AI 能力」组尾部当愿景位。
  const groups = useMemo(() => {
    const base: {
      labelKey: string;
      shortKey: string;
      visible: NavItem[];
      accent: boolean;
    }[] = sidebarTreeFor(scope).map((g) => ({
      labelKey: g.labelKey,
      shortKey: g.shortKey,
      // OEM W3:渠道树的项都不挂 moduleKey,不等 /me/permissions(渠道管理员没有租户模块矩阵)。
      visible: visibleByPartnerPerms(
        visibleByPerms(
          g.items,
          scope === "PARTNER" ? (modules ?? {}) : modules,
          scope,
          role,
          isImpersonating,
          licenseEnabled
        ),
        partnerPerms
      ).map((it) =>
        kefuNav.kefuOff && it.moduleKey === "ai_kefu"
          ? { ...it, badge: t("sidebar.kefuHumanOnlyBadge") }
          : it
      ),
      accent: "accent" in g && !!g.accent,
    }));
    if (comingSoonItems.length > 0) {
      // 「即将上线」是愿景位,收在「服务与洞察」尾部 —— 不能混进「创作」:
      // 那一组是今天就能点开干活的东西,掺进点不动的项会稀释它。
      const svc = base.find((g) => g.labelKey === "sidebar.groupServices");
      if (svc) svc.visible = [...svc.visible, ...comingSoonItems];
    }
    return base;
  }, [
    modules,
    scope,
    role,
    isImpersonating,
    comingSoonItems,
    licenseEnabled,
    partnerPerms,
    kefuNav.kefuOff,
    t,
  ]);

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="border-b border-sidebar-border px-4 py-4">
        <div className="flex items-center gap-2.5">
          {/* OEM 贴牌:品牌给了 logo 就换掉 Bot 图标(直营无 logo → 原样)。 */}
          {sidebarLogo ? (
            <img src={sidebarLogo} alt="" className="h-8 w-8 shrink-0 rounded-lg object-contain" />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sidebar-primary">
              <Bot className="h-4.5 w-4.5 text-sidebar-primary-foreground" />
            </div>
          )}
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-sm font-bold text-sidebar-accent-foreground tracking-wide">
                {t("sidebar.brandName")}
              </span>
              <span className="text-2xs text-sidebar-muted">{t("sidebar.brandSubtitle")}</span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent ref={sidebarRef} className="px-2 py-2">
        {dashboardVisible && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild isActive={currentPath === "/"}>
                    <NavLink
                      to="/"
                      end
                      activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      {!collapsed && <span>{t("nav.dashboard")}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {groups.map((g) => {
          if (g.visible.length === 0) return null;
          // 展开态全组走可折叠渲染(GSAP 折叠/stagger/活跃竖轨);icon 收窄态回落扁平图标。
          if (!collapsed) {
            return (
              <CollapsibleNavGroup
                key={g.labelKey}
                label={t(g.labelKey)}
                storageKey={`sidebar.group.open:${g.labelKey}`}
                accent={g.accent}
                items={g.visible.map((item) => ({
                  label: item.titleKey ? t(item.titleKey) : item.title,
                  url: item.url,
                  icon: item.icon,
                  comingSoon: item.comingSoon,
                  badge: item.badge,
                }))}
                isActive={(url) => isNavItemActive(currentPath, url)}
                comingSoonBadge={t("sidebar.comingSoonBadge")}
              />
            );
          }
          return (
            <SidebarGroup key={g.labelKey}>
              <SidebarGroupLabel className="text-sidebar-muted text-2xs uppercase tracking-widest">
                {!collapsed ? t(g.labelKey) : t(g.shortKey)}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {g.visible.map((item) => (
                    <SidebarMenuItem key={item.url}>
                      <SidebarMenuButton
                        asChild
                        isActive={isNavItemActive(currentPath, item.url)}
                        className={item.comingSoon ? "opacity-55" : undefined}
                      >
                        <NavLink
                          to={item.url}
                          activeClassName="bg-sidebar-accent text-sidebar-accent-foreground"
                        >
                          <item.icon className="h-4 w-4" />
                          {!collapsed && (
                            <span className="flex flex-1 items-center gap-1.5 text-[13px]">
                              {item.titleKey ? t(item.titleKey) : item.title}
                              {item.comingSoon && (
                                <span className="pointer-events-none ml-auto rounded bg-sidebar-border/60 px-1 py-px text-2xs leading-none text-sidebar-muted">
                                  {t("sidebar.comingSoonBadge")}
                                </span>
                              )}
                              {item.badge && (
                                <span
                                  data-testid="nav-item-badge"
                                  className="pointer-events-none ml-auto rounded bg-warning/15 px-1 py-px text-2xs leading-none text-warning"
                                >
                                  {item.badge}
                                </span>
                              )}
                            </span>
                          )}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border px-3 py-3">
        {!collapsed && (
          <div className="flex items-center gap-2 text-2xs text-sidebar-muted">
            <div className="h-1.5 w-1.5 rounded-full bg-success" />
            <span>{t("sidebar.systemRunning")}</span>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}
